import { Link, useNavigate, useParams } from "@remix-run/react";
import clsx from "clsx";

import Layout from "~/components/layout";
import { Button } from "~/components/ui/button";
import { H1, P } from "~/components/ui/typography";
import { useSession } from "~/hooks/use-session";
import { formatDuration, getSessionUrl } from "~/lib/utils";

import { DateEditor } from "../components/session-page/date-editor";
import { TitleEditor } from "../components/session-page/title-editor";
import { useIsMobile } from "../hooks/use-mobile";

export const Transcript: React.FC<{ sessionId?: string }> = ({
    sessionId = "",
}) => {
    const navigate = useNavigate();
    const { session, isLoading } = useSession(sessionId);

    const labels = session?.speakerLabels;
    const labelsByValue = labels?.reduce<Record<string, string>>(
        (acc, { label, value }) => ({
            ...acc,
            [value]: label,
        }),
        {},
    ) ?? { A: "Speaker 1", B: "Speaker 2" };

    if (isLoading) {
        return <Layout loading={true} />;
    }

    if (!session) {
        return (
            <div className="flex flex-col items-center justify-center gap-4">
                <H1 className="text-2xl font-semibold">Session Not Found</H1>
                <P className="text-center text-gray-600">
                    This session either doesn't exist or you don't have access
                    to it.
                </P>
                <Button onClick={() => navigate("/sessions")} variant="subtle">
                    Go back
                </Button>
            </div>
        );
    }

    // AIDEV-NOTE: For transcript-upload source, show plain markdown without speaker labels
    const isTranscriptUpload = session.source === "transcript-upload";

    if (isTranscriptUpload) {
        const transcriptText = session.transcript
            ?.map((message) => message.content)
            .join("\n\n");

        return (
            <div className="flex h-full grow flex-col gap-4 overflow-y-auto">
                <div className="whitespace-pre-wrap font-display text-[18px] leading-[1.95] tracking-[0.014em]">
                    {transcriptText}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full grow flex-col gap-4 overflow-y-auto">
            <div className="text-body text-md space-y-6 whitespace-pre-wrap ">
                {session.transcript?.map((message, index) => (
                    <div
                        key={index}
                        className="flex flex-col gap-2 font-display text-[18px] leading-[1.95] tracking-[0.014em]"
                    >
                        <div className="flex flex-row items-center gap-2 text-brand-forest-800">
                            <div className="mt-0 flex items-center gap-2 pb-0">
                                <span className="font-medium text-brand-forest-800">
                                    {/* AIDEV-NOTE: Fallback matters — a speaker
                                        missing from speakerLabels (e.g. an
                                        observer in a split session before
                                        labels existed) rendered as BLANK.
                                        Prefixing with "Speaker " assumed the
                                        value was an AssemblyAI diarization
                                        letter ("A" -> "Speaker A"), but split
                                        sessions store the person's NAME, which
                                        rendered as "Speaker Fran Facilitator".
                                        The bare value reads correctly in both
                                        cases when it isn't a single letter. */}
                                    {labelsByValue[message.speaker.trim()] ||
                                        (message.speaker.trim().length === 1
                                            ? `Speaker ${message.speaker.trim()}`
                                            : message.speaker.trim())}
                                </span>
                                <span className="font-normal text-gray-600">
                                    ({formatDuration(message.startTime / 1000)})
                                </span>
                            </div>
                        </div>
                        <div className="mt-0">{message.content}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TranscriptPage = () => {
    const { sessionId, sessionToken } = useParams();
    const { session, isLoading } = useSession(sessionId as string);

    if (isLoading) {
        return <Layout loading={true} />;
    }
    if (!session) {
        return <Layout loading={true} />;
    }

    const dateEditor = (
        <div className={"z-10 -ml-2 w-full"}>
            <DateEditor
                sessionId={sessionId!}
                currentDate={new Date(session.meetingStartTime)}
            />
        </div>
    );

    return (
        <Layout
            showLogout={true}
            hideLogo={true}
            title={
                <div
                    className={clsx(
                        "flex flex-col justify-center items-start gap-1 w-full -mt-2 -mb-2 md:-ml-4 ",
                    )}
                >
                    {dateEditor}
                    <TitleEditor
                        sessionId={sessionId as string}
                        currentTitle={session.title}
                    />
                </div>
            }
        >
            <Link
                to={getSessionUrl(sessionId as string, sessionToken)}
                className="w-full"
            >
                <Button variant="link" className="px-0">
                    Back to summary
                </Button>
            </Link>
            <div className="mt-8">
                <Transcript sessionId={sessionId} />
            </div>
        </Layout>
    );
};

export default TranscriptPage;
